import React, { useState, useEffect, useCallback, useRef } from "react";
import { API, Storage } from "aws-amplify";
import { listPortfolioImages } from "../../graphql/queries";
import {
  createPortfolioImage,
  deletePortfolioImage,
  updatePortfolioImage,
} from "../../graphql/mutations";

const CATEGORIES = [
  { id: "pavers", label: "Pavers" },
  { id: "concrete", label: "Concrete" },
  { id: "steps", label: "Steps" },
  { id: "retaining", label: "Retaining Walls" },
];

export default function PortfolioManager() {
  const [activeCategory, setActiveCategory] = useState("pavers");
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [imageUrls, setImageUrls] = useState({});

  // Drag-and-drop state
  const [draggedId, setDraggedId] = useState(null);
  const [dropTargetIdx, setDropTargetIdx] = useState(null);

  // Upload position picker state
  const [pendingFiles, setPendingFiles] = useState(null);
  const [insertPosition, setInsertPosition] = useState(1);
  const fileInputRef = useRef(null);

  const fetchImages = useCallback(async () => {
    try {
      setLoading(true);
      const result = await API.graphql({
        query: listPortfolioImages,
        variables: { limit: 500 },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      const items = result.data.listPortfolioImages.items.sort(
        (a, b) => a.order - b.order
      );
      setImages(items);

      // Resolve S3 URLs
      const urls = {};
      await Promise.all(
        items.map(async (img) => {
          try {
            urls[img.id] = await Storage.get(img.s3Key);
          } catch (err) {
            console.error("Error getting URL for", img.s3Key, err);
          }
        })
      );
      setImageUrls(urls);
    } catch (err) {
      console.error("Error fetching images:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchImages();
  }, [fetchImages]);

  const categoryImages = images.filter((img) => img.category === activeCategory);
  const sortedCategoryImages = [...categoryImages].sort((a, b) => a.order - b.order);

  // === Drag-and-drop handlers ===

  const handleDragStart = (e, imageId) => {
    setDraggedId(imageId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleDragOver = (e, idx) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTargetIdx(idx);
  };

  const handleDragLeave = () => {
    setDropTargetIdx(null);
  };

  const handleDragEnd = () => {
    setDraggedId(null);
    setDropTargetIdx(null);
  };

  const handleDrop = async (e, targetIdx) => {
    e.preventDefault();
    setDropTargetIdx(null);

    if (draggedId === null) return;

    const draggedIdx = sortedCategoryImages.findIndex((img) => img.id === draggedId);
    if (draggedIdx === -1 || draggedIdx === targetIdx) {
      setDraggedId(null);
      return;
    }

    // Reorder the array
    const reordered = [...sortedCategoryImages];
    const [moved] = reordered.splice(draggedIdx, 1);
    reordered.splice(targetIdx, 0, moved);

    // Assign new sequential order values
    const updates = [];
    const newImages = [...images];

    reordered.forEach((img, i) => {
      const newOrder = i + 1;
      if (img.order !== newOrder) {
        updates.push({ id: img.id, order: newOrder });
        const globalIdx = newImages.findIndex((g) => g.id === img.id);
        if (globalIdx !== -1) {
          newImages[globalIdx] = { ...newImages[globalIdx], order: newOrder };
        }
      }
    });

    // Optimistically update UI
    setImages(newImages);
    setDraggedId(null);

    // Batch-update changed images
    try {
      await Promise.all(
        updates.map((u) =>
          API.graphql({
            query: updatePortfolioImage,
            variables: { input: { id: u.id, order: u.order } },
            authMode: "AMAZON_COGNITO_USER_POOLS",
          })
        )
      );
    } catch (err) {
      console.error("Error saving reorder:", err);
      alert("Error saving new order. Please refresh and try again.");
      fetchImages();
    }
  };

  // === Upload with position picker ===

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
    const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];

    for (const file of files) {
      if (!ALLOWED_TYPES.includes(file.type)) {
        alert(`"${file.name}" is not a supported image type. Use JPEG, PNG, or WebP.`);
        e.target.value = "";
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        alert(`"${file.name}" exceeds the 10MB size limit.`);
        e.target.value = "";
        return;
      }
    }

    // Show position picker instead of uploading immediately
    setPendingFiles(files);
    setInsertPosition(sortedCategoryImages.length + 1);
  };

  const handleCancelUpload = () => {
    setPendingFiles(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleConfirmUpload = async () => {
    if (!pendingFiles || pendingFiles.length === 0) return;

    const files = pendingFiles;
    const position = Math.max(1, Math.min(insertPosition, sortedCategoryImages.length + 1));
    setPendingFiles(null);
    setUploading(true);

    try {
      // Shift existing images at or after the insert position
      const toShift = sortedCategoryImages.filter((img) => img.order >= position);
      if (toShift.length > 0) {
        const shiftUpdates = toShift.map((img) => ({
          id: img.id,
          order: img.order + files.length,
        }));

        await Promise.all(
          shiftUpdates.map((u) =>
            API.graphql({
              query: updatePortfolioImage,
              variables: { input: { id: u.id, order: u.order } },
              authMode: "AMAZON_COGNITO_USER_POOLS",
            })
          )
        );

        setImages((prev) =>
          prev.map((img) => {
            const shifted = shiftUpdates.find((u) => u.id === img.id);
            return shifted ? { ...img, order: shifted.order } : img;
          })
        );
      }

      // Upload new files at the target position(s)
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        const timestamp = Date.now();
        const safeName = file.name.replace(/[^a-zA-Z0-9._-]/g, "_");
        const s3Key = `portfolio/${activeCategory}/${timestamp}-${safeName}`;

        await Storage.put(s3Key, file, {
          contentType: file.type,
          level: "public",
        });

        const result = await API.graphql({
          query: createPortfolioImage,
          variables: {
            input: {
              category: activeCategory,
              s3Key,
              order: position + i,
              alt: file.name.replace(/\.[^.]+$/, ""),
            },
          },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        });

        const newImage = result.data.createPortfolioImage;
        const url = await Storage.get(s3Key);

        setImages((prev) => [...prev, newImage]);
        setImageUrls((prev) => ({ ...prev, [newImage.id]: url }));
      }
    } catch (err) {
      console.error("Error uploading:", err);
      alert("Error uploading image. Please try again.");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (image) => {
    if (!window.confirm("Delete this image?")) return;

    try {
      await Storage.remove(image.s3Key);
      await API.graphql({
        query: deletePortfolioImage,
        variables: { input: { id: image.id } },
        authMode: "AMAZON_COGNITO_USER_POOLS",
      });
      setImages((prev) => prev.filter((img) => img.id !== image.id));
      setImageUrls((prev) => {
        const copy = { ...prev };
        delete copy[image.id];
        return copy;
      });
    } catch (err) {
      console.error("Error deleting:", err);
      alert("Error deleting image. Please try again.");
    }
  };

  const handleReorder = async (image, direction) => {
    const sorted = [...categoryImages].sort((a, b) => a.order - b.order);
    const idx = sorted.findIndex((img) => img.id === image.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;

    if (swapIdx < 0 || swapIdx >= sorted.length) return;

    const other = sorted[swapIdx];
    const tempOrder = image.order;

    try {
      await Promise.all([
        API.graphql({
          query: updatePortfolioImage,
          variables: { input: { id: image.id, order: other.order } },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        }),
        API.graphql({
          query: updatePortfolioImage,
          variables: { input: { id: other.id, order: tempOrder } },
          authMode: "AMAZON_COGNITO_USER_POOLS",
        }),
      ]);

      setImages((prev) =>
        prev.map((img) => {
          if (img.id === image.id) return { ...img, order: other.order };
          if (img.id === other.id) return { ...img, order: tempOrder };
          return img;
        })
      );
    } catch (err) {
      console.error("Error reordering:", err);
    }
  };

  return (
    <div className="portfolio-manager">
      {/* Category Tabs */}
      <div className="pm-tabs">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            className={`pm-tab ${activeCategory === cat.id ? "active" : ""}`}
            onClick={() => setActiveCategory(cat.id)}
          >
            {cat.label}
            <span className="pm-tab-count">
              {images.filter((img) => img.category === cat.id).length}
            </span>
          </button>
        ))}
      </div>

      {/* Upload */}
      <div className="pm-upload-section">
        <label className="pm-upload-btn">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M17 8l-5-5-5 5M12 3v12" />
          </svg>
          {uploading ? "Uploading..." : "Upload Images"}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            disabled={uploading || pendingFiles !== null}
            style={{ display: "none" }}
          />
        </label>

        {/* Position Picker */}
        {pendingFiles && (
          <div className="pm-position-picker">
            <span>
              Insert {pendingFiles.length} image{pendingFiles.length > 1 ? "s" : ""} at position:
            </span>
            <input
              type="number"
              min={1}
              max={sortedCategoryImages.length + 1}
              value={insertPosition}
              onChange={(e) => setInsertPosition(Number(e.target.value))}
            />
            <span className="pm-position-total">of {sortedCategoryImages.length + 1}</span>
            <button className="pm-position-confirm" onClick={handleConfirmUpload}>
              Upload
            </button>
            <button className="pm-position-cancel" onClick={handleCancelUpload}>
              Cancel
            </button>
          </div>
        )}
      </div>

      {/* Image Grid */}
      {loading ? (
        <div className="admin-loading">Loading images...</div>
      ) : categoryImages.length === 0 ? (
        <div className="pm-empty">
          No images in this category yet. Upload some above.
        </div>
      ) : (
        <div className="pm-grid">
          {sortedCategoryImages.map((image, idx) => (
              <div
                className={
                  "pm-card" +
                  (draggedId === image.id ? " dragging" : "") +
                  (dropTargetIdx === idx && draggedId !== image.id ? " drop-target" : "")
                }
                key={image.id}
                draggable
                onDragStart={(e) => handleDragStart(e, image.id)}
                onDragOver={(e) => handleDragOver(e, idx)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, idx)}
                onDragEnd={handleDragEnd}
              >
                <div className="pm-card-image">
                  {imageUrls[image.id] ? (
                    <img src={imageUrls[image.id]} alt={image.alt || ""} />
                  ) : (
                    <div className="pm-card-placeholder">Loading...</div>
                  )}
                </div>
                <div className="pm-card-actions">
                  <button
                    className="pm-btn-reorder"
                    onClick={() => handleReorder(image, "up")}
                    disabled={idx === 0}
                    title="Move up"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 15l-6-6-6 6" />
                    </svg>
                  </button>
                  <button
                    className="pm-btn-reorder"
                    onClick={() => handleReorder(image, "down")}
                    disabled={idx === categoryImages.length - 1}
                    title="Move down"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M6 9l6 6 6-6" />
                    </svg>
                  </button>
                  <span className="pm-card-order">#{image.order}</span>
                  <button
                    className="pm-btn-delete"
                    onClick={() => handleDelete(image)}
                    title="Delete"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
        </div>
      )}
    </div>
  );
}
